// src/components/TabsWrapper.js

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Tabs, Tab, Box, TextField, Typography, Paper, Grid, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Select, MenuItem, Button, IconButton,
  Card, CardContent, Divider, Chip, Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ScaleIcon from '@mui/icons-material/Scale';
import CountertopsIcon from '@mui/icons-material/Countertops';
import LocalLaundryServiceIcon from '@mui/icons-material/LocalLaundryService';
import SpeedIcon from '@mui/icons-material/Speed';
import ReminderBar from '../components/ReminderBar';
import { CLIENTS } from '../data/clients';

// ✅ Імпорт сервісу зберігання
import { saveSessionItem } from '../services/storageService';
// ✅ Імпорт хука для i18n
import { useI18n } from '../i18n/i18n';

// ✅ ВИПРАВЛЕННЯ: Повертаємо 'textFieldStyles'
const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': {
      borderColor: '#1976d2',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1976d2',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#1976d2',
  }
};

function num(v) { const n = Number(v); return isNaN(n) || n < 0 ? 0 : n; }

// Компонент для заголовків секцій
const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 1, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
    <Icon sx={{ fontSize: 28, color: '#1976d2', mr: 2 }} />
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

// Компонент для відображення загальних підсумків
const TotalDisplay = ({ label, value, unit = '', color = 'primary' }) => (
  <Card variant="outlined" sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa' }}>
    <Typography variant="h6" align="center" sx={{ fontWeight: 'bold' }}>
      {label}: <Chip label={`${value} ${unit}`} color={color} variant="filled" />
    </Typography>
  </Card>
);

// ОНОВЛЕНИЙ КОМПОНЕНТ ДЛЯ ТАБЛИЦЬ "LAVADORAS" З ШТРИХАМИ
function CyclesTableUnified({ values, setValues, labels, isRechazos = false }) {
  // ✅ Отримуємо функцію t
  const { t } = useI18n();
  const WEIGHTS = ['20', '40', '60'];
  
  const getVal = (lbl, w) => values[`${lbl}-${w}`] || '';
  
  const changeVal = (lbl, w, dir) => {
    const key = `${lbl}-${w}`;
    const currentVal = getVal(lbl, w);
    // ✅ ВИПРАВЛЕННЯ NaN: Рахуємо довжину рядка, а не split
    const currentCount = (currentVal || "").length;
    const newCount = Math.max(0, currentCount + dir);
    const newValue = '|'.repeat(newCount);
    setValues({ ...values, [key]: newValue });
  };

  const calculateTotals = () => {
    const totals = { '20': 0, '40': 0, '60': 0, overall: 0 };
    
    labels.forEach(({ key }) => {
      WEIGHTS.forEach(w => {
        const val = getVal(key, w);
        // ✅ ВИПРАВЛЕННЯ NaN: Рахуємо довжину рядка
        const count = (val || "").length;
        totals[w] += count;
        totals.overall += count;
      });
    });
    
    return totals;
  };

  const totals = calculateTotals();

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                {/* ✅ i18n */}
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{t('labels_program')}</TableCell>
                {WEIGHTS.map((w) => (
                  <TableCell key={w} align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    {w} {t('labels_kg')}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {labels.map(({ key, label }) => (
                <TableRow key={key} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'medium' }}>
                    {label} {/* 'label' вже приходить перекладеним з useMemo */}
                  </TableCell>
                  {WEIGHTS.map((w) => {
                    const currentVal = getVal(key, w);
                    const displayValue = currentVal || '';
                    return (
                      <TableCell key={w} align="center">
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          minHeight: '50px'
                        }}>
                          <IconButton 
                            size="small" 
                            onClick={() => changeVal(key, w, -1)}
                            color="error"
                            sx={{ 
                              border: '1px solid #ff4444',
                              '&:hover': { backgroundColor: '#ff4444', color: 'white' }
                            }}
                          >
                            <RemoveIcon />
                          </IconButton>
                          <Typography 
                            sx={{ 
                              minWidth: '80px', 
                              textAlign: 'center',
                              fontFamily: 'monospace',
                              fontSize: '1.2rem',
                              padding: '6px 12px',
                              border: '2px solid #e0e0e0',
                              borderRadius: '8px',
                              backgroundColor: '#ffffff',
                              minHeight: '40px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: '0 8px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          >
                            {displayValue}
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={() => changeVal(key, w, +1)}
                            color="success"
                            sx={{ 
                              border: '1px solid #00c851',
                              '&:hover': { backgroundColor: '#00c851', color: 'white' }
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              {/* Рядок загальних підсумків по стовпцях */}
              <TableRow sx={{ backgroundColor: '#e8f5e8' }}>
                {/* ✅ i18n */}
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{t('labels_total_column')}</TableCell>
                {WEIGHTS.map((w) => (
                  <TableCell key={w} align="center" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    <Chip label={totals[w]} color="primary" variant="outlined" />
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Загальний підсумок */}
        <Box sx={{ mt: 2, p: 2, backgroundColor: '#2c3e50', borderRadius: 2 }}>
          <Typography variant="h6" align="center" sx={{ fontWeight: 'bold', color: 'white' }}>
            {/* ✅ i18n */}
            {t('labels_total_general')}: <Chip label={totals.overall} color="secondary" sx={{ color: 'white', fontSize: '1.1rem' }} />
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

// === ГОЛОВНИЙ КОМПОНЕНТ ===
export default function TabsWrapper({ user, shiftData }) {
  // ✅ Отримуємо функцію t
  const { t } = useI18n();
  const [tabIndex, setTabIndex] = useState(0);
  
  const [formData, setFormData] = useState(() => ({
    ...shiftData,
    pesoCliente: shiftData.pesoCliente || [{ client: '', program: '', weight: '' }],
  }));

  useEffect(() => {
    const saveCurrentData = async () => {
        await saveSessionItem('current_shift', formData);
    };
    saveCurrentData();
  }, [formData]);

  const updateField = (section, field, value) => { 
    setFormData(p => ({ ...p, [section]: { ...p[section], [field]: value } })); 
  };
  
  const updateSection = (section, newValues) => { 
    setFormData(p => ({ ...p, [section]: newValues })); 
  };
  
  const totalTunel = useMemo(() => 
    ['pro1', 'pro2', 'pro3', 'pro4', 'pro6', 'pro8', 'pro9', 'pro10', 'pro14']
      .reduce((acc, key) => acc + num(formData.tunel[key]), 0), 
    [formData.tunel]
  );
  
  const totalRechTunel = useMemo(() => 
    ['r11', 'r12', 'r13', 'rotas']
      .reduce((acc, key) => acc + num(formData.rech_tunel[key]), 0), 
    [formData.rech_tunel]
  );

  // ✅ Динамічно генеруємо списки на основі перекладів
  const PROGRAMS = useMemo(() => [
    { code: '01', name: t('programs_01') }, { code: '02', name: t('programs_02') }, { code: '03', name: t('programs_03') },
    { code: '04', name: t('programs_04') }, { code: '06', name: t('programs_06') }, { code: '08', name: t('programs_08') },
    { code: '09', name: t('programs_09') }, { code: '10', name: t('programs_10') }, { code: '11', name: t('programs_11') },
    { code: '12', name: t('programs_12') }, { code: '13', name: t('programs_13') }, { code: '14', name: t('programs_14') },
    { code: 'otros', name: t('programs_otros') },
  ], [t]);

  const tunnelLabels = useMemo(() => [
    { key: 'pro1', label: t('programs_01_full') }, { key: 'pro2', label: t('programs_02_full') }, 
    { key: 'pro3', label: t('programs_03_full') }, { key: 'pro4', label: t('programs_04_full') }, 
    { key: 'pro6', label: t('programs_06_full') }, { key: 'pro8', label: t('programs_08_full') }, 
    { key: 'pro9', label: t('programs_09_full') }, { key: 'pro10', label: t('programs_10_full') }, 
    { key: 'pro14', label: t('programs_14_full') }
  ], [t]);

  const tunnelRechazoLabels = useMemo(() => [
    { key: 'r11', label: t('programs_11_full') }, { key: 'r12', label: t('programs_12_full') }, 
    { key: 'r13', label: t('programs_13_full') }, { key: 'rotas', label: t('programs_rotas') }
  ], [t]);

  const cyclesLabels = useMemo(() => [
    { key: 'mant', label: t('cycles_manteleria') }, { key: 'sab', label: t('cycles_sabanas') }, { key: 'pis', label: t('cycles_piscina') },
    { key: 'otr', label: t('cycles_otros') }, { key: 'otr-29', label: t('cycles_otros_29') }, { key: 'otr-30', label: t('cycles_otros_30') },
    { key: 'cent', label: t('cycles_centrifugo') },
  ], [t]);
  
  const rechazosLabels = useMemo(() => [ 
    { key: 'toallas', label: t('rejects_toallas') }, { key: 'nordicos', label: t('rejects_nordicos') }, 
    { key: 'sabanas', label: t('rejects_sabanas') }, { key: 'otros', label: t('rejects_otros') } 
  ], [t]);

  const meterLabelsWater = useMemo(() => [
    { key: 'aguaTunel', label: t('meters_agua_tunel'), unit: 'm³' }, 
    { key: 'aguaPozo', label: t('meters_agua_pozo'), unit: 'm³' }, 
    { key: 'aguaLav', label: t('meters_agua_lavadoras'), unit: 'm³' }, 
    { key: 'aguaCal', label: t('meters_agua_calderas'), unit: 'm³' }
  ], [t]);

  const meterLabelsPropane = useMemo(() => [
    { key: 'propDep', label: t('meters_prop_deposito'), unit: '%' }, 
    { key: 'propCaldera', label: t('meters_prop_caldera'), unit: 'm³' }, 
    { key: 'propSaba', label: t('meters_prop_saba'), unit: 'm³' }, 
    { key: 'propMedio', label: t('meters_prop_medio'), unit: 'm³' }, 
    { key: 'propHibri', label: t('meters_prop_hibri'), unit: 'm³' }, 
    { key: 'propSec', label: t('meters_prop_secadoras'), unit: 'm³' }
  ], [t]);


  const handleWeightTableUpdate = (newRows) => updateSection('pesoCliente', newRows);
  const addWeightRow = () => handleWeightTableUpdate([...(formData.pesoCliente || []), { client: '', program: '', weight: '' }]);

  // Стилі для табів
  const tabStyles = {
    fontWeight: 'bold',
    fontSize: '0.9rem',
    '&.Mui-selected': {
      color: '#1976d2',
      fontWeight: 'bold'
    }
  };

  return (
    <Paper elevation={2} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
      <ReminderBar shift={formData.turno} role={user?.role} />
      
      <Tabs 
        value={tabIndex} 
        onChange={(_, val) => setTabIndex(val)} 
        variant="scrollable" 
        scrollButtons="auto"
        sx={{ 
          backgroundColor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
          '& .MuiTabs-indicator': {
            backgroundColor: '#1976d2',
            height: 3
          }
        }}
      >
        {/* ✅ i18n назви вкладок */}
        <Tab icon={<CountertopsIcon />} iconPosition="start" label={t('tabs_tunnel_production')} sx={tabStyles} />
        <Tab icon={<LocalLaundryServiceIcon />} iconPosition="start" label={t('tabs_washers')} sx={tabStyles} />
        <Tab icon={<SpeedIcon />} iconPosition="start" label={t('tabs_meters')} sx={tabStyles} />
        <Tab icon={<ScaleIcon />} iconPosition="start" label={t('tabs_client_weight')} sx={tabStyles} />
      </Tabs>
      
      <Box sx={{ p: 3 }}>
        {tabIndex === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <SectionHeader 
                    icon={CountertopsIcon} 
                    /* ✅ i18n */
                    title={t('tunnel_prod_title')}
                    subtitle={t('tunnel_prod_subtitle')}
                  />
                  
                  <Stack spacing={2}>
                    {/* ✅ Використання динамічного масиву */}
                    {tunnelLabels.map(({ key, label }) => (
                      <TextField 
                        key={key}
                        label={label}
                        type="number"
                        size="small"
                        fullWidth
                        value={formData.tunel[key] || ''}
                        onChange={(e) => updateField('tunel', key, num(e.target.value))}
                        sx={textFieldStyles}
                        InputProps={{
                          endAdornment: <Typography variant="body2" color="text.secondary">{t('labels_kg')}</Typography>
                        }}
                      />
                    ))}
                  </Stack>
                  
                  <TotalDisplay 
                    /* ✅ i18n */
                    label={t('labels_total_tunnel')}
                    value={totalTunel} 
                    unit={t('labels_kg')} 
                    color="primary" 
                  />
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <SectionHeader 
                    icon={CountertopsIcon} 
                    /* ✅ i18n */
                    title={t('tunnel_reject_title')} 
                    subtitle={t('tunnel_reject_subtitle')}
                  />
                  
                  <Stack spacing={2}>
                    {/* ✅ Використання динамічного масиву */}
                    {tunnelRechazoLabels.map(({ key, label }) => (
                      <TextField 
                        key={key}
                        label={label}
                        type="number"
                        size="small"
                        fullWidth
                        value={formData.rech_tunel[key] || ''}
                        onChange={(e) => updateField('rech_tunel', key, num(e.target.value))}
                        sx={textFieldStyles}
                        InputProps={{
                          endAdornment: <Typography variant="body2" color="text.secondary">{t('labels_kg')}</Typography>
                        }}
                      />
                    ))}
                  </Stack>
                  
                  <TotalDisplay 
                    /* ✅ i18n */
                    label={t('labels_total_reject')} 
                    value={totalRechTunel} 
                    unit={t('labels_kg')} 
                    color="error" 
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
        
        {tabIndex === 1 && (
          <Box>
            <Card variant="outlined" sx={{ mb: 4 }}>
              <CardContent>
                <SectionHeader 
                  icon={LocalLaundryServiceIcon} 
                  /* ✅ i18n */
                  title={t('washers_cycles_title')} 
                  subtitle={t('washers_cycles_subtitle')}
                />
                <CyclesTableUnified 
                  values={formData.lav_ciclos || {}} 
                  setValues={(v) => updateSection('lav_ciclos', v)} 
                  labels={cyclesLabels} /* labels вже перекладені з useMemo */
                />
              </CardContent>
            </Card>
            
            <Card variant="outlined">
              <CardContent>
                <SectionHeader 
                  icon={LocalLaundryServiceIcon} 
                  /* ✅ i18n */
                  title={t('washers_rejects_title')} 
                  subtitle={t('washers_rejects_subtitle')}
                />
                <CyclesTableUnified 
                  values={formData.lav_rechazos || {}} 
                  setValues={(v) => updateSection('lav_rechazos', v)} 
                  labels={rechazosLabels} /* labels вже перекладені з useMemo */
                  isRechazos={true}
                />
              </CardContent>
            </Card>
          </Box>
        )}
        
        {tabIndex === 2 && (
          <Card variant="outlined">
            <CardContent>
              <SectionHeader 
                icon={SpeedIcon} 
                /* ✅ i18n */
                title={t('meters_title')} 
                subtitle={t('meters_subtitle')}
              />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    {/* ✅ Використання динамічного масиву */}
                    {meterLabelsWater.map(({ key, label, unit }) => (
                      <TextField 
                        key={key}
                        label={`${label} [${unit}]`}
                        type="number"
                        size="small"
                        value={formData.cont[key] || ''}
                        onChange={(e) => updateField('cont', key, num(e.target.value))}
                        sx={textFieldStyles}
                        fullWidth
                      />
                    ))}
                  </Stack>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    {/* ✅ Використання динамічного масиву */}
                    {meterLabelsPropane.map(({ key, label, unit }) => (
                      <TextField 
                        key={key}
                        label={`${label} [${unit}]`}
                        type="number"
                        size="small"
                        value={formData.cont[key] || ''}
                        onChange={(e) => updateField('cont', key, num(e.target.value))}
                        sx={textFieldStyles}
                        fullWidth
                      />
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
        
        {tabIndex === 3 && (
          <Card variant="outlined">
            <CardContent>
              <SectionHeader 
                icon={ScaleIcon} 
                /* ✅ i18n */
                title={t('client_weight_title')} 
                subtitle={t('client_weight_subtitle')}
              />
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                      {/* ✅ i18n */}
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('labels_client')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('labels_program')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('labels_weight_kg')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(formData.pesoCliente || []).map((row, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Select 
                            size="small" 
                            fullWidth 
                            value={row.client} 
                            onChange={(e) => handleWeightTableUpdate(formData.pesoCliente.map((r, i) => i === index ? { ...r, client: e.target.value } : r))}
                            sx={textFieldStyles}
                          >
                            {/* ✅ i18n */}
                            <MenuItem value=""><em>{t('placeholders_select_client')}</em></MenuItem>
                            {CLIENTS.map(c => <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>)}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select 
                            size="small" 
                            fullWidth 
                            value={row.program} 
                            onChange={(e) => handleWeightTableUpdate(formData.pesoCliente.map((r, i) => i === index ? { ...r, program: e.target.value } : r))}
                            sx={textFieldStyles}
                          >
                            {/* ✅ i18n */}
                            <MenuItem value=""><em>{t('placeholders_select_program')}</em></MenuItem>
                            {/* ✅ Використання динамічного масиву */}
                            {PROGRAMS.map(p => <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>)}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <TextField 
                            size="small" 
                            fullWidth 
                            placeholder="0.00" 
                            value={row.weight} 
                            onChange={(e) => handleWeightTableUpdate(formData.pesoCliente.map((r, i) => i === index ? { ...r, weight: e.target.value } : r))}
                            sx={textFieldStyles}
                            InputProps={{
                              /* ✅ i18n */
                              endAdornment: <Typography variant="body2" color="text.secondary">{t('labels_kg')}</Typography>
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Button 
                onClick={addWeightRow} 
                startIcon={<AddIcon />} 
                variant="outlined"
                sx={{ mt: 2, fontWeight: 'bold' }}
              >
                {/* ✅ i18n */}
                {t('buttons_add_row')}
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>
    </Paper>
  );
}


